// src/App.js

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import SplashScreen from "./components/SplashScreen/SplashScreen";
import LoginSystem from "./components/LoginSystem/LoginSystem";
import PrivateRoute from "./components/PrivateRoute/PrivateRoute";
import MainLayout from "./components/MainLayout/MainLayout";
import AdminPage from "./pages/AdminPage/AdminPage";
import EmployeePage from "./pages/EmployeePage/EmployeePage";
import MaintenancePage from "./pages/MaintenancePage/MaintenancePage";
import AttendanceContent from "./components/AttendanceContent/AttendanceContent";
import ReportsContent from "./components/ReportsContent/ReportsContent";
import SettingsContent from "./components/SettingsContent/SettingsContent";
import PersonnelList from "./components/PersonnelList/PersonnelList";
import WeeklySummary from "./pages/EmployeePage/WeeklySummary";
import MonthlyStatus from "./pages/EmployeePage/MonthlyStatus";
import PersonalInfo from "./pages/EmployeePage/PersonnelInfo";
import AttendanceFlow from "./components/AttendanceFlow/AttendanceFlow";
import ResetPassword from "./components/ResetPassword/ResetPassword";
import PermissionsContent from "./components/PermissionsContent/PermissionsContent";
import WorkScheduleContent from "./components/WorkScheduleContent/WorkScheduleContent";
import EmployeeLeaveRequest from "./components/EmployeeLeaveRequest/EmployeeLeaveRequest";
import { supabase } from "./supabaseClient";
import { clearAuth, getUser, getToken } from "./services/authService";
import { OrbitLoaderComponent } from "./utils/orbitLoader";
import { NotificationProvider } from "./components/NotificationContext/NotificationContext";
import "./App.css";

function AppWrapper() {
  return (
    <Router>
      <NotificationProvider>
        <App />
      </NotificationProvider>
    </Router>
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  const applyTheme = (role) => {
    if (role === "admin") {
      document.documentElement.setAttribute("data-theme", "admin");
    } else if (role === "maintenance_admin") {
      document.documentElement.setAttribute("data-theme", "maintenance");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      setSession(null);
      setUserRole(null);
      document.documentElement.removeAttribute("data-theme");
      await clearAuth();
      sessionStorage.setItem("isAfterLogout", "true");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === "SIGNED_IN" && session) {
        setSession(session);
        const role = session.user.user_metadata?.role || "employee";
        setUserRole(role);
        applyTheme(role);
      } else if (event === "SIGNED_OUT") {
        setSession(null);
        setUserRole(null);
        document.documentElement.removeAttribute("data-theme");
        if (!isLoggingOut) {
          sessionStorage.setItem("isAfterLogout", "true");
          navigate("/login");
        }
      }
    });
    setLoading(false);
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [isLoggingOut, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoggingOut) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} color="white" />
        <p>Çıkış yapılıyor...</p>
      </div>
    );
  }

  if (showSplash) {
    return <SplashScreen />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} color="white" />
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<LoginSystem />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/employee"
          element={
            <PrivateRoute requiredRole="employee">
              <MainLayout
                onLogout={handleLogout}
                userData={session?.user}
                pageType="employee"
              />
            </PrivateRoute>
          }
        >
          <Route
            index
            element={<EmployeePage userData={session?.user} demoMode={false} />}
          />
          <Route path="weekly-summary" element={<WeeklySummary />} />
          <Route path="monthly-status" element={<MonthlyStatus />} />
          <Route path="personal-info" element={<PersonalInfo />} />
          <Route path="leave-request" element={<EmployeeLeaveRequest />} />
          <Route
            path="attendance"
            element={<AttendanceFlow userId={session?.user?.id} />}
          />
        </Route>
        <Route
          path="/admin"
          element={
            <PrivateRoute requiredRole="admin">
              <MainLayout
                onLogout={handleLogout}
                userData={session?.user}
                pageType="admin"
              />
            </PrivateRoute>
          }
        >
          <Route
            index
            element={<AdminPage userData={session?.user} demoMode={false} />}
          />
          <Route path="personnel" element={<PersonnelList />} />
          <Route path="attendance" element={<AttendanceContent />} />
          <Route path="reports" element={<ReportsContent />} />
          <Route path="settings" element={<SettingsContent />} />
          <Route path="permissions" element={<PermissionsContent />} />
          <Route path="work-schedule" element={<WorkScheduleContent />} />
        </Route>
        <Route
          path="/maintenance"
          element={
            <PrivateRoute requiredRole="maintenance_admin">
              <MaintenancePage
                userData={session?.user}
                onLogout={handleLogout}
              />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<MaintenancePage />} />
          {/* YENİ: Doğrudan admin ve employee sayfalarına yönlendirme */}
          <Route path="admin" element={<Navigate to="/admin" replace />} />
          <Route
            path="employee"
            element={<Navigate to="/employee" replace />}
          />
          <Route path="admin/*" element={<AdminPage inMaintenance={true} />} />
          <Route
            path="employee/*"
            element={<EmployeePage inMaintenance={true} />}
          />
          <Route path="logs" element={<MaintenancePage />} />
          <Route path="settings" element={<MaintenancePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
}

export default AppWrapper;
